"use client";

import TiltCard from "@/components/ui/TiltCard";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";
import Link from "next/link";
import { useState } from "react";

export interface Testimonial {
    id?: string;
    quote?: string;
    text?: string; // fallback
    author: string;
    role: string;
    company?: string;
}

interface TestimonialsProps {
    testimonials?: Testimonial[];
}

export default function Testimonials({ testimonials = [] }: TestimonialsProps) {
    const defaultTestimonials: Testimonial[] = [
        {
            quote: "DharmaTech built our MVP in just 4 weeks. Their speed and code quality are unmatched in the industry.",
            author: "Sarah J.",
            role: "Founder, FinX",
            company: "FinX"
        },
        {
            quote: "The AI integration they added to our platform increased user retention by 40%. Truly transformative.",
            author: "Michael C.",
            role: "CTO",
            company: "HealthAI"
        },
        {
            quote: "Reliable, fast, and transparent. The best development partner we've worked with in years.",
            author: "David L.",
            role: "Product Manager",
            company: "LogiTech"
        }
    ];

    const displayTestimonials = testimonials.length > 0 ? testimonials : defaultTestimonials;

    const itemsPerPage = 3;
    const [currentPage, setCurrentPage] = useState(0);
    const totalPages = Math.ceil(displayTestimonials.length / itemsPerPage);

    const nextPage = () => {
        setCurrentPage((prev) => (prev + 1) % totalPages);
    };

    const prevPage = () => {
        setCurrentPage((prev) => (prev - 1 + totalPages) % totalPages);
    };

    const currentTestimonials = displayTestimonials.slice(
        currentPage * itemsPerPage,
        (currentPage + 1) * itemsPerPage
    );

    const TestimonialContent = ({ t }: { t: Testimonial }) => (
        <TiltCard
            className="bg-zinc-900 border border-zinc-800 p-8 h-full transition-colors hover:border-zinc-700 rounded-2xl"
            innerClassName="h-full flex flex-col"
        >
            <div className="flex mb-6">
                {[1, 2, 3, 4, 5].map((s) => (
                    <Star key={s} className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                ))}
            </div>
            <p className="text-lg leading-relaxed mb-8 text-gray-300 flex-1">"{t.quote || t.text}"</p>
            <div className="mt-auto">
                <p className="font-bold text-white text-xl">{t.author}</p>
                <p className="text-sm text-gray-400">{t.role}</p>
            </div>
        </TiltCard>
    );

    return (
        <section className="py-24 bg-black text-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <h2 className="text-3xl md:text-5xl font-bold mb-16 text-center tracking-tight">Don't just take our word for it.</h2>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    <AnimatePresence mode="wait">
                        {currentTestimonials.map((t, index) => (
                            <motion.div
                                key={t.author + currentPage}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ duration: 0.3, delay: index * 0.1 }}
                                className="h-full"
                            >
                                {t.id ? (
                                    <Link href={`/case-studies/${t.id}`} className="block h-full group cursor-pointer">
                                        <TestimonialContent t={t} />
                                    </Link>
                                ) : (
                                    <TestimonialContent t={t} />
                                )}
                            </motion.div>
                        ))}
                    </AnimatePresence>
                </div>

                {totalPages > 1 && (
                    <div className="flex justify-center items-center mt-12 gap-6">
                        <button
                            onClick={prevPage}
                            className="p-2 rounded-full border border-zinc-800 hover:bg-zinc-800 hover:text-white transition-colors text-gray-400"
                            aria-label="Previous page"
                        >
                            <ChevronLeft className="h-6 w-6" />
                        </button>
                        <div className="flex gap-2">
                            {Array.from({ length: totalPages }).map((_, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => setCurrentPage(idx)}
                                    className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${idx === currentPage ? 'bg-white w-8' : 'bg-zinc-800 hover:bg-zinc-600'
                                        }`}
                                    aria-label={`Go to page ${idx + 1}`}
                                />
                            ))}
                        </div>
                        <button
                            onClick={nextPage}
                            className="p-2 rounded-full border border-zinc-800 hover:bg-zinc-800 hover:text-white transition-colors text-gray-400"
                            aria-label="Next page"
                        >
                            <ChevronRight className="h-6 w-6" />
                        </button>
                    </div>
                )}
            </div>
        </section>
    );
}
